/**
 * Super Admin Onboarding Tab Component
 * Displays a table of all users in onboarding process with management capabilities
 */

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  MoreHorizontal, 
  Settings,
  Users,
  Building
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useSuperAdminOnboarding } from '@/hooks/useSuperAdminOnboarding';
import { 
  getStatusBadgeVariant, 
  getStatusDisplayName,
  OnboardingTableRow 
} from '@/types/super-admin-onboarding';
import { OnboardingStatus } from '@/types/onboarding';
import { getRoleDisplayName, getRoleBadgeVariant } from '@/utils/role-utils';
import OnboardingManagementDialog from './onboarding/OnboardingManagementDialog';

interface SuperAdminOnboardingTabProps {
  organisations: any[];
}

const SuperAdminOnboardingTab: React.FC<SuperAdminOnboardingTabProps> = ({ organisations }) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [managementDialogOpen, setManagementDialogOpen] = useState(false);

  const {
    onboardingData,
    loading,
    error,
    pagination,
    filters,
    setFilters,
    refetch,
    setPage,
    setPageSize
  } = useSuperAdminOnboarding({
    page: 1,
    limit: 10,
    initialFilters: {
      role: 'new_sign_up,in_progress'
    }
  });

  // Handle opening management dialog
  const handleManageOnboarding = (userId: string) => {
    setSelectedUserId(userId);
    setManagementDialogOpen(true);
  };

  // Handle closing management dialog
  const handleCloseManagement = () => {
    setSelectedUserId(null);
    setManagementDialogOpen(false);
    refetch(); // Refresh data when dialog closes
  };

  // Get progress color based on percentage
  const getProgressColor = (percentage: number): string => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };


  // Get stage status icon
  const getStageStatusIcon = (status?: OnboardingStatus) => {
    switch (status) {
      case OnboardingStatus.COMPLETE:
        return <div className="w-3 h-3 bg-green-500 rounded-full" title="Complete" />;
      case OnboardingStatus.AWAITING_CONFIRMATION:
        return <div className="w-3 h-3 bg-yellow-500 rounded-full" title="Awaiting Confirmation" />;
      case OnboardingStatus.NEEDS_ATTENTION:
        return <div className="w-3 h-3 bg-red-500 rounded-full" title="Needs Attention" />;
      default:
        return <div className="w-3 h-3 bg-gray-300 rounded-full" title="Not Started" />;
    }
  };

  // Format date as dd/mm/yyyy
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-4 w-full max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Onboarding Management</h2>
            <p className="text-muted-foreground">
              Manage users in the onboarding process
            </p>
          </div>
        </div>
        
        <div className="flex flex-col gap-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="font-medium">Overall Status:</span>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              Complete
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              In Progress
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              Needs Attention
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">Stage Order:</span>
            <span className="text-xs">Business • Stripe • Printer • Domain • Website • Google</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
          <Input
            placeholder="Search by name or email..."
            className="pl-8"
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
          />
        </div>
        
        <Select
          value={filters.role}
          onValueChange={(value) => setFilters({ role: value })}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="new_sign_up,in_progress">All Onboarding</SelectItem>
            <SelectItem value="new_sign_up">New Sign Up</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.organisation_id || 'all'}
          onValueChange={(value) => setFilters({ 
            organisation_id: value === 'all' ? undefined : value 
          })}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by organisation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Organisations</SelectItem>
            <SelectItem value="none">No Organisation</SelectItem>
            {organisations.map(org => (
              <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.status || 'all'}
          onValueChange={(value) => setFilters({ 
            status: value === 'all' ? undefined : value as OnboardingStatus 
          })}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value={OnboardingStatus.NOT_STARTED}>Not Started</SelectItem>
            <SelectItem value={OnboardingStatus.AWAITING_CONFIRMATION}>Awaiting Confirmation</SelectItem>
            <SelectItem value={OnboardingStatus.NEEDS_ATTENTION}>Needs Attention</SelectItem>
            <SelectItem value={OnboardingStatus.COMPLETE}>Complete</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Main Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto scrollbar-hide">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Restaurant</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Status Breakdown</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array(pagination.limit).fill(0).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <Skeleton className="h-2 w-full" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-destructive">
                    <div className="flex flex-col items-center gap-2">
                      <p>Error loading onboarding data</p>
                      <p className="text-sm text-muted-foreground">{error}</p>
                      <Button variant="outline" size="sm" onClick={refetch}>
                        Try Again
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : onboardingData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Building className="h-8 w-8" />
                      <p>No users in onboarding found</p>
                      <p className="text-sm">Try adjusting your filters</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                onboardingData.map((row: OnboardingTableRow) => (
                  <TableRow key={row.id} className="group">
                    <TableCell>
                      <button
                        onClick={() => handleManageOnboarding(row.user_id)}
                        className="space-y-1 text-left w-full group/name"
                      >
                        <div className="font-medium cursor-pointer transition-colors group-hover/name:text-primary group-active/name:text-primary">{row.user_name}</div>
                        <div className="text-sm text-muted-foreground">{row.user_email}</div>
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(row.user_role)}>
                        {getRoleDisplayName(row.user_role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {row.restaurant_name ? (
                        <button
                          onClick={() => handleManageOnboarding(row.user_id)}
                          className="text-left w-full group/restaurant"
                        >
                          <div className="font-medium cursor-pointer transition-colors group-hover/restaurant:text-primary group-active/restaurant:text-primary">{row.restaurant_name}</div>
                          {row.organisation_name && (
                            <div className="text-sm text-muted-foreground">{row.organisation_name}</div>
                          )}
                        </button>
                      ) : (
                        <div className="text-muted-foreground italic">Not set</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Progress 
                            value={row.progress_percentage} 
                            className="flex-1 h-2"
                          />
                          <span className="text-xs text-muted-foreground w-12">
                            {row.progress_percentage}%
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {row.completed_stages}/{row.total_stages} stages complete
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <div className="flex items-center gap-1">
                          {getStageStatusIcon(row.business_details_status)}
                          {getStageStatusIcon(row.stripe_connection_status)}
                          {getStageStatusIcon(row.printer_setup_status)}
                          {getStageStatusIcon(row.dns_status)}
                          {getStageStatusIcon(row.website_linked_status)}
                          {getStageStatusIcon(row.google_ordering_status)}
                        </div>
                        <Badge variant={getStatusBadgeVariant(row.onboarding_status)} className="text-xs">
                          {getStatusDisplayName(row.onboarding_status)}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(row.updated_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleManageOnboarding(row.user_id)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Manage Onboarding
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>

          {/* Pagination */}
          {!loading && !error && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {onboardingData.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.max(1, pagination.page - 1))}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm">
                  Page {pagination.page} of {pagination.totalPages || 1}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.min(pagination.totalPages, pagination.page + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Management Dialog */}
      {selectedUserId && (
        <OnboardingManagementDialog
          open={managementDialogOpen}
          onOpenChange={setManagementDialogOpen}
          userId={selectedUserId}
          onClose={handleCloseManagement}
        />
      )}
    </div>
  );
};

export default SuperAdminOnboardingTab;